import React, { useEffect } from 'react';
import gql from 'graphql-tag';
import { useForm, FormProvider } from 'react-hook-form';
import { useMutation } from '@apollo/client';
import { useSelector } from 'react-redux';
import { injectIntl, FormattedMessage } from 'react-intl';
import { Icon, Message } from 'semantic-ui-react';
import { omit } from 'lodash';
import { formatNumber } from 'libphonenumber-js';

import { Modal, Button } from '../../../../components';
import { AdministratorForm } from '../form';

const UPDATE_ADMINISTRATOR_MUTATION = gql`
    mutation updateAdministrator($input: UpdateAdministratorInput!) {
        updateAdministrator(input: $input) {
            administrator {
                id
                firstName
                lastName
                phone
                email
            }
        }
    }
`;

function buildInitialValues(administrator) {
    return {
        firstName: administrator.firstName || '',
        lastName: administrator.lastName || '',
        phone: administrator.phone ? formatNumber(administrator.phone, 'International') : '',
        email: administrator.email || '',
        password: '',
        passwordRepeat: '',
    };
}

function PersonalInformation({ administrator, open, onClose, intl }) {
    const userId = useSelector(({ user }) => user.get('id'));
    const salon = useSelector(({ user }) => user.get('salon'));

    const methods = useForm({ defaultValues: buildInitialValues(administrator) });
    const { handleSubmit, reset, setError, formState: { isSubmitting, isDirty } } = methods;

    useEffect(() => {
        if (open) {
            reset(buildInitialValues(administrator));
        }
    }, [administrator.id, open]); // eslint-disable-line react-hooks/exhaustive-deps

    const [updateAdministrator] = useMutation(UPDATE_ADMINISTRATOR_MUTATION);

    const onSubmit = async formData => {
        const phone = (formData.phone || '').replace(/[^+\d]/g, '');
        try {
            await updateAdministrator({
                variables: {
                    input: {
                        userId,
                        salonId: salon,
                        administratorId: administrator.id,
                        ...omit(formData, 'passwordRepeat'),
                        phone,
                    },
                },
                optimisticResponse: {
                    __typename: 'Mutation',
                    updateAdministrator: {
                        __typename: 'UpdateAdministratorPayload',
                        administrator: {
                            __typename: 'Administrator',
                            id: administrator.id,
                            firstName: formData.firstName,
                            lastName: formData.lastName,
                            phone,
                            email: formData.email,
                        },
                    },
                },
            });
            onClose();
        } catch (error) {
            const graphQLError = error.graphQLErrors && error.graphQLErrors[0];
            setError('root', {
                message: (graphQLError && graphQLError.message) || 'Something went wrong',
            });
        }
    };

    return (
        <FormProvider {...methods}>
            <Modal.Context>
                <Modal.Content>
                    {!administrator.phone && (
                        <Message error>
                            <Icon name="warning sign" />
                            <FormattedMessage
                                id="pages.administrators.form.phoneRequired"
                                defaultMessage="Please enter a valid phone number"
                            />
                        </Message>
                    )}
                    <AdministratorForm mode="edit" />
                </Modal.Content>
                <Modal.Actions>
                    <Button
                        onClick={handleSubmit(onSubmit)}
                        disabled={isSubmitting || !isDirty}
                        loading={isSubmitting}
                        primary
                    >
                        <FormattedMessage
                            id="pages.administrators.form.save"
                            defaultMessage="Save"
                        />
                    </Button>
                    <Button onClick={onClose} disabled={isSubmitting} basic>
                        <Icon name="remove" />
                        <FormattedMessage
                            id="pages.administrators.form.cancel"
                            defaultMessage="Cancel"
                        />
                    </Button>
                </Modal.Actions>
            </Modal.Context>
        </FormProvider>
    );
}

export default injectIntl(PersonalInformation);
